import { useMemo, useState } from 'react';
import { CalendarDays, Check, MapPin, Mic, RotateCcw, ShieldCheck, Type } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { IntakeExtraction } from '../../shared/intake.js';
import { ITEM_CATEGORIES, ITEM_COLORS } from '../../shared/report-schema.js';
import { api, recordFunnelEvent } from '../api.js';
import { AppShell } from '../components/AppShell.js';
import { Button, Field, Input, Notice, Textarea } from '../components/ui.js';
import { copy } from '../i18n.js';
import { useRealtimeIntake } from '../realtime/useRealtimeIntake.js';
import { useDraft } from '../state/DraftContext.js';
import { applyIntakeExtraction, resetIntake } from '../state/draft.js';

interface TextParseResult extends IntakeExtraction {
  providerUsed: boolean;
}

export function IntakePage() {
  const navigate = useNavigate();
  const { draft, update } = useDraft();
  if (!draft) return null;
  return <IntakeContent draft={draft} update={update} navigate={navigate} />;
}

function IntakeContent({
  draft,
  update,
  navigate,
}: {
  draft: NonNullable<ReturnType<typeof useDraft>['draft']>;
  update: ReturnType<typeof useDraft>['update'];
  navigate: ReturnType<typeof useNavigate>;
}) {
  const t = copy[draft.language];
  const [textMode, setTextMode] = useState(draft.usedTextFallback);
  const [text, setText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState(false);
  const voice = useRealtimeIntake({
    language: draft.language,
    onExtraction: (extraction) => update((current) => applyIntakeExtraction(current, extraction)),
  });
  const intakeComplete = Boolean(draft.item.description);

  const canContinue = Boolean(
    draft.item.description.trim() && draft.lostDate && !draft.dateInFuture,
  );
  const voiceLabel =
    voice.state === 'connecting'
      ? t.intake.connecting
      : voice.state === 'listening'
        ? t.intake.listening
        : voice.state === 'thinking'
          ? t.intake.thinking
          : t.intake.voice;
  const transcript = useMemo(
    () => [
      ...voice.transcript,
      ...(voice.assistantDraft ? [{ role: 'ASSISTANT' as const, text: voice.assistantDraft }] : []),
    ],
    [voice.assistantDraft, voice.transcript],
  );

  const parseText = async () => {
    if (text.trim().length < 3) return;
    setParsing(true);
    setParseError(false);
    try {
      const result = await api<TextParseResult>('/api/intake/parse-text', {
        method: 'POST',
        body: JSON.stringify({ text, language: draft.language }),
      });
      setText(result.redactedText);
      update((current) =>
        applyIntakeExtraction(current, {
          source: 'TEXT',
          text: result.redactedText,
          item: {
            ...result.item,
            distinguishingFeatures: result.item.distinguishingFeatures ?? '',
          },
          lostDate: result.lostDate,
          dateInFuture: result.dateInFuture,
          place: result.place,
          walletPolicyRequired: result.walletPolicyRequired,
          confidence: result.confidence,
          clarificationCount: 0,
          extractionToolCallCount: 0,
        }),
      );
      recordFunnelEvent({
        type: 'TEXT_FALLBACK',
        step: 'INTAKE',
        source: 'TEXT',
        language: draft.language,
      });
    } catch {
      setParseError(true);
    } finally {
      setParsing(false);
    }
  };

  const switchToText = () => {
    voice.stop();
    setTextMode(true);
    update({ usedTextFallback: true });
    recordFunnelEvent({ type: 'TEXT_FALLBACK', step: 'INTAKE', language: draft.language });
  };

  const dateValue = draft.lostDate?.kind === 'KNOWN' ? draft.lostDate.start : '';
  const startOver = () => {
    setText('');
    setTextMode(false);
    setParseError(false);
    update((current) => resetIntake(current));
    void voice.restart();
  };
  return (
    <AppShell language={draft.language} step={1}>
      <header className="page-heading">
        <h1>{intakeComplete ? t.intake.completedTitle : t.intake.title}</h1>
        <p>{intakeComplete ? t.intake.completedLead : t.intake.lead}</p>
        <span className="venue-context">
          <MapPin size={15} /> {draft.venue.name}, {draft.venue.city}
        </span>
      </header>

      {!intakeComplete ? (
        <section className="flow-card intake-card">
          {!textMode ? (
            <div className="voice-panel">
              <button
                type="button"
                className={`voice-button voice-${voice.state}`}
                onClick={() => void voice.start()}
                disabled={['connecting', 'listening', 'thinking'].includes(voice.state)}
                aria-label={voiceLabel}
              >
                <span className="voice-ring" aria-hidden />
                <Mic size={32} aria-hidden />
                <span>{voiceLabel}</span>
              </button>
              {voice.state === 'denied' ? (
                <Notice tone="warning">{t.intake.micDenied}</Notice>
              ) : null}
              {voice.state === 'error' ? (
                <Notice tone="danger">{t.intake.unavailable}</Notice>
              ) : null}
              {voice.state !== 'idle' ? (
                <button className="text-action" type="button" onClick={() => void voice.restart()}>
                  <RotateCcw size={15} /> {t.intake.restart}
                </button>
              ) : null}
              <button className="text-action" type="button" onClick={switchToText}>
                <Type size={15} /> {t.intake.textLink}
              </button>
            </div>
          ) : (
            <div className="text-panel">
              <Field label={t.intake.textLabel}>
                <Textarea
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  placeholder={t.intake.textPlaceholder}
                  rows={5}
                  autoFocus
                />
              </Field>
              {parseError ? <Notice tone="danger">{t.intake.unavailable}</Notice> : null}
              <Button onClick={() => void parseText()} disabled={parsing || text.trim().length < 3}>
                {parsing ? t.intake.thinking : t.intake.parse}
              </Button>
              <button className="text-action" type="button" onClick={() => setTextMode(false)}>
                <Mic size={15} /> {t.intake.voice}
              </button>
            </div>
          )}
          <p className="privacy-note">
            <ShieldCheck size={15} aria-hidden /> {t.intake.privacy}
          </p>
        </section>
      ) : null}

      {!intakeComplete && transcript.length ? (
        <section className="transcript" aria-live="polite">
          {transcript.map((entry, index) => (
            <p
              className={`bubble bubble-${entry.role.toLowerCase()}`}
              key={`${entry.role}-${index}`}
            >
              {entry.text}
            </p>
          ))}
        </section>
      ) : null}

      {draft.item.description ? (
        <section className="flow-card extracted-card completed-intake-card" aria-live="polite">
          <h2>
            <Check size={20} /> {t.intake.heard}
          </h2>
          <div className="field-grid">
            <Field label={t.intake.item}>
              <Input
                value={draft.item.description}
                onChange={(event) =>
                  update((current) => ({
                    ...current,
                    item: { ...current.item, description: event.target.value },
                    mandatoryFieldCorrections: current.mandatoryFieldCorrections + 1,
                  }))
                }
              />
            </Field>
            <Field label="Category">
              <select
                className="input"
                value={draft.item.category}
                onChange={(event) =>
                  update((current) => ({
                    ...current,
                    item: {
                      ...current.item,
                      category: event.target.value as typeof current.item.category,
                    },
                  }))
                }
              >
                {ITEM_CATEGORIES.map((category) => (
                  <option value={category} key={category}>
                    {category}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Color">
              <select
                className="input"
                value={draft.item.color}
                onChange={(event) =>
                  update((current) => ({
                    ...current,
                    item: {
                      ...current.item,
                      color: event.target.value as typeof current.item.color,
                    },
                  }))
                }
              >
                {ITEM_COLORS.map((color) => (
                  <option value={color} key={color}>
                    {color}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t.intake.when}>
              <div className="inline-field">
                <Input
                  type="date"
                  max={new Date().toISOString().slice(0, 10)}
                  value={dateValue}
                  onChange={(event) =>
                    update({
                      lostDate: event.target.value
                        ? { kind: 'KNOWN', start: event.target.value, end: event.target.value }
                        : null,
                      dateInFuture: event.target.value > new Date().toISOString().slice(0, 10),
                    })
                  }
                />
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => update({ lostDate: { kind: 'UNKNOWN' }, dateInFuture: false })}
                >
                  {t.common.unknown}
                </Button>
              </div>
            </Field>
            <div className="detected-line">
              <CalendarDays size={17} />
              <span>
                {draft.lostDate?.kind === 'KNOWN'
                  ? draft.lostDate.start
                  : draft.lostDate?.kind === 'UNKNOWN'
                    ? t.common.unknown
                    : t.common.required}
              </span>
            </div>
            <div className="detected-line">
              <MapPin size={17} />
              <span>{placeLabel(draft.place, t.common.unknown)}</span>
            </div>
          </div>
          {draft.dateInFuture ? <Notice tone="danger">{t.intake.future}</Notice> : null}
          <p className="privacy-note">
            <ShieldCheck size={15} aria-hidden /> {t.intake.privacy}
          </p>
        </section>
      ) : null}

      <div className={`flow-actions ${intakeComplete ? '' : 'single'}`}>
        {intakeComplete ? (
          <Button variant="secondary" onClick={startOver}>
            <RotateCcw size={16} aria-hidden /> {t.intake.startOver}
          </Button>
        ) : null}
        <Button disabled={!canContinue} onClick={() => navigate('/report/place')}>
          {t.common.continue}
        </Button>
      </div>
    </AppShell>
  );
}

function placeLabel(
  place: NonNullable<ReturnType<typeof useDraft>['draft']>['place'],
  unknown: string,
) {
  if (!place || place.kind === 'UNKNOWN') return unknown;
  return place.name;
}
